import React, { useState, useRef } from "react";
import { App } from "antd";
import {
  UploadOutlined, FilePdfOutlined, SortAscendingOutlined,
  DownloadOutlined, DeleteOutlined, CheckCircleFilled, HolderOutlined,
} from "@ant-design/icons";
import axios from "axios";
import HeaderLayout from "../../components/Header";
import FooterLayout from "../../components/Footer";
import PageHeader from "../../components/PageHeader";
import { downloadAuthenticatedFile } from "../../utils/downloadFile";
import "../PdfTools.css";
import "./RearrangePdf.css";

const countPdfPages = async (pdfFile) => {
  const buffer = await pdfFile.arrayBuffer();
  const pdfText = new TextDecoder("latin1").decode(buffer);
  const pageMatches = pdfText.match(/\/Type\s*\/Page\b(?!s)/g);

  if (pageMatches?.length) {
    return pageMatches.length;
  }

  const countMatches = [...pdfText.matchAll(/\/Count\s+(\d+)/g)]
    .map((match) => Number(match[1]))
    .filter((count) => Number.isFinite(count) && count > 0);

  return countMatches.length ? Math.max(...countMatches) : 0;
};

const RearrangePdf = () => {
  const { message } = App.useApp();
  const [file,        setFile]        = useState(null);
  const [uploading,   setUploading]   = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [readingPages, setReadingPages] = useState(false);
  const [pageReadFailed, setPageReadFailed] = useState(false);
  const [dragOver,    setDragOver]    = useState(false);
  const [pageCount,   setPageCount]   = useState(0);
  const [pageOrder,   setPageOrder]   = useState([]); // 1-based page numbers in current order
  const [downloadUrl, setDownloadUrl] = useState("");
  const [dragging,    setDragging]    = useState(null);
  const fileRef = useRef(null);

  const isModule = window.location.pathname.includes("/dashboard/");

  const initPages = (count) => {
    setPageCount(count);
    setPageOrder(Array.from({ length: count }, (_, i) => i + 1));
  };

  const pickFile = async (incoming) => {
    const f = Array.from(incoming).find((f) =>
      f.type === "application/pdf" || f.name.endsWith(".pdf")
    );
    if (!f) { message.error("Please select a PDF file."); return; }
    setFile(f);
    setDownloadUrl("");
    setDownloading(false);
    setPageOrder([]);
    setPageCount(0);
    setPageReadFailed(false);
    setReadingPages(true);

    try {
      const detectedPageCount = await countPdfPages(f);
      if (detectedPageCount > 0) {
        initPages(detectedPageCount);
      } else {
        setPageReadFailed(true);
        message.warning("Unable to read page count automatically. Please enter it manually.");
      }
    } catch {
      setPageReadFailed(true);
      message.warning("Unable to read page count automatically. Please enter it manually.");
    } finally {
      setReadingPages(false);
    }
  };

  const reset = () => {
    setFile(null);
    setDownloadUrl("");
    setDownloading(false);
    setPageOrder([]);
    setPageCount(0);
    setPageReadFailed(false);
    setReadingPages(false);
  };

  // Drag reorder
  const onDragStart = (i) => setDragging(i);
  const onDragOver  = (e, i) => {
    e.preventDefault();
    if (dragging === null || dragging === i) return;
    setPageOrder((prev) => {
      const next = [...prev];
      next.splice(i, 0, next.splice(dragging, 1)[0]);
      return next;
    });
    setDragging(i);
  };
  const onDragEnd = () => setDragging(null);

  const moveUp   = (i) => { if (i === 0) return; setPageOrder((p) => { const n=[...p]; [n[i-1],n[i]]=[n[i],n[i-1]]; return n; }); };
  const moveDown = (i) => { if (i === pageOrder.length-1) return; setPageOrder((p) => { const n=[...p]; [n[i],n[i+1]]=[n[i+1],n[i]]; return n; }); };

  const handleRearrange = async () => {
    if (!file || pageOrder.length === 0) return;
    setUploading(true);
    setDownloadUrl("");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("Source", isModule ? "Private-Web" : "Public-Web");
    pageOrder.forEach((p, i) => formData.append(`rearrangedPages[${i}]`, p.toString()));

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Pdf/rearrange-pdf-pages`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (!res.data.error && res.data.data?.downloadUrl) {
        setDownloadUrl(res.data.data.downloadUrl);
        message.success("PDF rearranged successfully!");
      } else {
        message.error(res.data.message || "Rearrange failed.");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setUploading(false);
    }
  };

  const downloadRearrangedPdf = async () => {
    if (!downloadUrl || downloading) return;
    setDownloading(true);

    try {
      await downloadAuthenticatedFile(downloadUrl, {
        token: localStorage.getItem("token") ?? "",
        fileName: "rearranged.pdf",
      });
    } catch {
      message.error("Unable to download PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      {isModule ? <PageHeader /> : <HeaderLayout />}

      <div className={`pt-root ${isModule ? "pt-module" : ""}`}>
        <div className="pt-inner">

          <div className="pt-header">
            <div className="pt-header-icon" style={{ background: "#eff6ff" }}>
              <SortAscendingOutlined style={{ color: "#0891b2" }} />
            </div>
            <div>
              <h1 className="pt-title">Rearrange PDF</h1>
              <p className="pt-subtitle">Drag and drop pages to reorder them, then download your new PDF.</p>
            </div>
          </div>

          {!file && !downloadUrl && (
            <div
              className={`pt-dropzone ${dragOver ? "drag-over" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept=".pdf,application/pdf" style={{ display: "none" }}
                onChange={(e) => { pickFile(e.target.files); e.target.value = ""; }} />
              <FilePdfOutlined className="pt-dropzone-icon" />
              <div className="pt-dropzone-text">Click to select or drag your PDF here</div>
              <div className="pt-dropzone-hint">Only PDF files supported</div>
              <button className="pt-choose-btn" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}>
                <UploadOutlined /> Choose File
              </button>
            </div>
          )}

          {file && !downloadUrl && (
            <>
              <div className="pt-file-row">
                <FilePdfOutlined className="pt-file-icon" />
                <span className="pt-file-name">{file.name}</span>
                <span className="pt-file-size">{(file.size / 1024).toFixed(0)} KB</span>
                <button className="pt-file-remove" onClick={reset}><DeleteOutlined /></button>
              </div>

              {(readingPages || pageReadFailed || pageOrder.length > 0) && (
                <div className="rp-page-count-wrap">
                  <label className="rp-page-label">
                    {readingPages
                      ? "Reading PDF page count..."
                      : pageReadFailed
                        ? "How many pages does this PDF have?"
                        : "PDF pages detected"}
                  </label>
                  {readingPages ? (
                    <span className="rp-page-reading">
                      <span className="pt-spinner" /> Checking
                    </span>
                  ) : pageReadFailed ? (
                    <>
                      <input
                        type="number"
                        min="1"
                        max="500"
                        className="rp-page-input"
                        placeholder="e.g. 8"
                        onBlur={(e) => { const v = parseInt(e.target.value); if (v > 0) initPages(v); }}
                      />
                      <button
                        className="pt-choose-btn"
                        style={{ marginTop: 0 }}
                        onClick={(e) => { const v = parseInt(e.currentTarget.previousSibling.value); if (v > 0) initPages(v); }}
                      >
                        Set Pages
                      </button>
                    </>
                  ) : (
                    <span className="rp-page-detected">{pageCount} page{pageCount !== 1 ? "s" : ""}</span>
                  )}
                </div>
              )}

              {pageOrder.length > 0 && (
                <>
                  <div className="rp-hint">
                    <HolderOutlined /> Drag rows to reorder · Use arrows for precise control
                  </div>
                  <div className="rp-list">
                    {pageOrder.map((pageNum, i) => (
                      <div
                        key={pageNum}
                        className={`rp-row ${dragging === i ? "dragging" : ""}`}
                        draggable
                        onDragStart={() => onDragStart(i)}
                        onDragOver={(e) => onDragOver(e, i)}
                        onDragEnd={onDragEnd}
                      >
                        <span className="rp-position">{i + 1}</span>
                        <HolderOutlined className="rp-drag-handle" />
                        <FilePdfOutlined className="rp-page-icon" />
                        <span className="rp-page-label-item">Page {pageNum}</span>
                        <div className="rp-arrows">
                          <button className="rp-arrow" onClick={() => moveUp(i)} disabled={i === 0}>▲</button>
                          <button className="rp-arrow" onClick={() => moveDown(i)} disabled={i === pageOrder.length - 1}>▼</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button className="pt-action-btn" onClick={handleRearrange} disabled={uploading}>
                    {uploading ? <span className="pt-spinner" /> : <SortAscendingOutlined />}
                    {uploading ? "Processing…" : "Save New Order"}
                  </button>
                </>
              )}
            </>
          )}

          {downloadUrl && (
            <div className="pt-success">
              <CheckCircleFilled className="pt-success-icon" />
              <div className="pt-success-title">Rearrangement Complete!</div>
              <p className="pt-success-sub">Your PDF pages have been reordered successfully.</p>
              <div className="pt-success-actions">
                <button
                  type="button"
                  className="pt-download-btn"
                  onClick={downloadRearrangedPdf}
                  disabled={downloading}
                >
                  {downloading ? <span className="pt-spinner" /> : <DownloadOutlined />}
                  {downloading ? "Downloading..." : "Download PDF"}
                </button>
                <button className="pt-restart-btn" onClick={reset}>Rearrange another</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {!isModule && <FooterLayout />}
    </>
  );
};

export default RearrangePdf;
